import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';

import { Image, Typography } from '../../atoms';
import { ImageDimensionsType } from '../../atoms/Image/Image';

export interface GroupHeaderProps {
  isActive?: boolean;
  src: string;
  title: string;
  variant?: 'board' | 'tile';
  warningMsg?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  groupHeader: {
    alignItems: 'center',
    backgroundColor: theme.palette.grey[200],
    border: `2px solid ${theme.palette.grey[200]}`,
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'flex-start',
    padding: theme.spacing(0.25),

    '& .fd-text': {
      color: theme.palette.grey[800],
      display: '-webkit-box',
      overflow: 'hidden',
      paddingLeft: theme.spacing(1),
      wordBreak: 'break-word',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
    },

    '& .fd-image': {
      maxWidth: 'initial',
    },

    '& .groupHeaderImage': {
      border: `2px solid ${theme.palette.common.white}`,
      height: '28px',
      width: '28px',
    },
  },

  active: {
    backgroundColor: theme.palette.common.white,
    border: `2px solid ${theme.palette.primary.main}`,
  },

  tile: {
    maxWidth: '240px',
    minHeight: '56px',
    padding: theme.spacing(1, 2, 1, 1),

    '& .fd-text': {
      color: theme.palette.text.primary,
      paddingLeft: theme.spacing(1.25),
    },

    '& .groupHeaderImage': {
      height: '36px',
      minWidth: '36px',

      '.Mui-disabled & img': {
        filter: 'grayscale(1)',
      },
    },
  },

  warningMsg: {
    '&.fd-text': {
      color: theme.palette.error.main,
      fontSize: theme.typography.pxToRem(12),
    },
  },
}));

export function GroupHeader(props: GroupHeaderProps): JSX.Element {
  const {
    isActive = false,
    src,
    title,
    variant = 'board',
    warningMsg = '',
  } = props;

  const classes = useStyles();

  const imageDimensions: ImageDimensionsType =
    variant === 'board' ? [24, 24] : [32, 32];

  const groupHeaderStyles = clsx(
    classes.groupHeader,
    'fd-group-header',
    isActive && variant === 'tile' && classes.active,
    variant === 'tile' && classes.tile,
  );

  return (
    <div className={groupHeaderStyles}>
      {src && (
        <div className="groupHeaderImage">
          <Image alt={title} dimensions={imageDimensions} src={src} />
        </div>
      )}

      <div>
        <Typography variant="caption">{title}</Typography>

        {warningMsg && (
          <Typography className={classes.warningMsg}>{warningMsg}</Typography>
        )}
      </div>
    </div>
  );
}
